import { createSlice } from '@reduxjs/toolkit';
import { formsService } from '../services/formsService';
import { db } from '../lib/database';

const initialState = {
  forms: [],
  status: 'idle',
  error: null,
  currentForm: null
};

const formsSlice = createSlice({
  name: 'forms',
  initialState,
  reducers: {
    setFormsLoading: state => {
      state.status = 'loading';
    },
    setFormsSuccess: (state, action) => {
      state.forms = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    setFormsFailure: (state, action) => {
      state.status = 'failed';
      state.error = action.payload;
    },
    setCurrentForm: (state, action) => {
      state.currentForm = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    clearCurrentForm: state => {
      state.currentForm = null;
    },
    resetForms: () => initialState
  }
});

export const { setFormsLoading, setFormsSuccess, setFormsFailure, setCurrentForm, clearCurrentForm, resetForms } = formsSlice.actions;

const processForms = (forms, submissions) => {
  const submissionCounts = submissions.reduce((acc, sub) => {
    const formId = String(sub.form_id);
    acc[formId] = (acc[formId] || 0) + 1;
    return acc;
  }, {});

  return forms.map(form => ({
    ...form,
    title: form.name || form.title,
    submissions: submissionCounts[String(form.id)] || 0,
    isCompleted: (submissionCounts[String(form.id)] || 0) > 0
  }));
};

const mapQuestionsToSections = questions => {
  if (!questions || questions.length === 0) return [];

  const sectionMap = new Map();

  questions.forEach(question => {
    const sectionId = question.section_id;

    if (!sectionMap.has(sectionId)) {
      sectionMap.set(sectionId, {
        id: sectionId,
        name: question.section_name,
        order: question.section_order,
        fields: []
      });
    }
    sectionMap.get(sectionId).fields.push(question);
  });

  const sections = Array.from(sectionMap.values());
  sections.sort((a, b) => (a.order || 0) - (b.order || 0));
  sections.forEach(section => {
    section.fields.sort((a, b) => (a.order || 0) - (b.order || 0));
  });
  return sections;
};

export const fetchForms = () => dispatch => {
  dispatch(setFormsLoading());

  const loadFromDb = () => {
    return formsService.getAllLocal().then(localForms => {
      return db.submissions.toArray().then(localSubmissions => {
        const processed = processForms(localForms, localSubmissions);
        return processed;
      });
    });
  };

  if (navigator.onLine) {
    formsService
      .syncAll()
      .then(updatedForms => {
        return db.submissions.toArray().then(localSubmissions => {
          const processed = processForms(updatedForms, localSubmissions);
          dispatch(setFormsSuccess(processed));
        });
      })
      .catch(error => {
        loadFromDb().then(processed => {
          if (processed.length > 0) {
            dispatch(setFormsSuccess(processed));
          } else {
            dispatch(setFormsFailure(error.message));
          }
        });
      });
  } else {
    loadFromDb().then(processed => {
      if (processed.length > 0) {
        dispatch(setFormsSuccess(processed));
      } else {
        dispatch(setFormsFailure('No forms available offline.'));
      }
    });
  }
};

export const fetchFormById = id => (dispatch, getState) => {
  const { currentForm } = getState().forms;

  if (!currentForm || String(currentForm.id) !== String(id)) {
    dispatch(setFormsLoading());
  }

  const loadFromDb = () => {
    return formsService.getLocalById(id).then(localForm => {
      if (localForm) {
        // Parse questions from DB (where options are stored as strings)
        const parsedQuestions = (localForm.questions || []).map(q => ({
          ...q,
          // Ensure options are an array of objects. DB stores them as JSON strings.
          options: q.options ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) : []
        }));

        localForm.questions = parsedQuestions;
        // Only rebuild sections if they are missing metadata (like relevance or is_repeat)
        if (!localForm.sections || localForm.sections.length === 0) {
          localForm.sections = mapQuestionsToSections(parsedQuestions);
        }

        dispatch(setCurrentForm(localForm));
        return true;
      }
      return false;
    });
  };

  loadFromDb().then(loadedLocal => {
    if (navigator.onLine) {
      formsService
        .syncDetails(id)
        .then(apiForm => {
          if (apiForm) {
            loadFromDb();
          } else if (!loadedLocal) {
            dispatch(setFormsFailure('Form not found'));
          }
        })
        .catch(error => {
          if (!loadedLocal) {
            dispatch(setFormsFailure(error.message || 'Failed to load form.'));
          }
        });
    } else if (!loadedLocal) {
      dispatch(setFormsFailure('Form not available offline.'));
    }
  });
};

export default formsSlice.reducer;
